import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Camera, Check, Download, Trash2, Video, X } from 'lucide-react';

import { AuthGuard } from '../../app/AuthGuard';
import { hasAcceptedOffer } from '../../features/carrierCabinet/transportAcceptance';
import { CabinetEmptyState, CabinetLoadState, ProfileLink } from '../../ui';
import { isCompletedTransport } from '../../features/carrierCabinet/transportStatus';
import type { ScreenId } from '../../app/screenRoutes';
import {
  carrierCabinetApi,
  type BillingSummaryDto,
  type BillingTransactionDto,
  type CarrierProfileDto,
  type DisputeDto,
  type FileAssetDto,
  type InvoiceDto,
  type ReviewDto,
  type TransportDto,
  type TransportRequestDto,
} from '../../features/carrierCabinet/api';
import { CarrierCabinetLayout, navigateCarrier } from '../../layouts/CarrierCabinet/CarrierCabinetLayout';
import { openPhotoGallery } from '../../utils/photoGallery';
import { readStoredLanguage } from '../../i18n/locale';
import { useLanguage } from '../../i18n/LanguageProvider';
import { getRuntimeTimezone } from '../../i18n/preferences';
import { CarrierOfferScreen } from './CarrierOfferScreen';
import {
  budgetLabel,
  carrierBoxLabel,
  dateLabel,
  dateTimeLabel,
  documentsStatusLabel,
  hashNumber,
  money,
  PageHeading,
  petDetails,
} from './carrierCabinetShared';
import { payInvoiceByCardLabel, unpaidInvoiceTitle } from './carrierInvoiceLabels';
import './carrier-work.css';

export type CarrierWorkScreenId = Extract<
  ScreenId,
  'carrier-active-transport' | 'carrier-completed' | 'carrier-earnings'
>;

const FIGMA_NODES: Record<CarrierWorkScreenId, { '1920': string; '1440': string }> = {
  'carrier-active-transport': { '1920': '2683:62597', '1440': '2721:50329' },
  'carrier-completed': { '1920': '2686:63347', '1440': '2691:64437' },
  'carrier-earnings': { '1920': '2693:65675', '1440': '2699:66498' },
};

const ACTIVE_TRANSPORT_STATUSES = new Set(['planned', 'in_progress']);

function isActiveTransport(transport: TransportDto) {
  if (!hasAcceptedOffer(transport) || isCompletedTransport(transport)) return false;
  if (ACTIVE_TRANSPORT_STATUSES.has(transport.status)) return true;
  return transport.status === 'delivered'
    && !transport.customer_received_at
    && transport.request_status !== 'completed';
}

function ActiveTransportDetail({ label, value }: { label: string; value: ReactNode }) {
  if (value == null || (typeof value === 'string' && value.trim() === '')) return null;
  return <div><span>{label}</span><strong>{value}</strong></div>;
}

function shortDate(value: string | null | undefined) {
  if (!value) return '';
  return new Intl.DateTimeFormat(readStoredLanguage() === 'en' ? 'en-GB' : 'ru-RU', {
    day: 'numeric',
    month: 'long',
    ...(value.includes('T') ? { timeZone: getRuntimeTimezone() } : {}),
  }).format(new Date(value));
}

function paymentStatusLabel(status: string) {
  if (status === 'carrier_confirmed') return 'Оплата подтверждена';
  if (status === 'customer_marked_paid') return 'Заказчик отметил оплату';
  if (status === 'agreed_direct') return 'Прямая оплата согласована';
  return 'Ожидает согласования';
}

function tripStatusLabel(transport: TransportDto) {
  if (transport.status === 'planned') return 'Готова к старту';
  if (transport.status === 'delivered') return 'Доставлено';
  if (transport.status === 'closed') return 'Завершено';
  const start = transport.departure_date ? new Date(`${transport.departure_date}T00:00:00`) : new Date(transport.created_at);
  const end = transport.delivery_date ? new Date(`${transport.delivery_date}T00:00:00`) : start;
  const today = new Date();
  const total = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000) + 1);
  const current = Math.min(total, Math.max(1, Math.floor((today.getTime() - start.getTime()) / 86400000) + 1));
  return `День ${current}/${total} · в пути`;
}

function petFullDetails(request: TransportRequestDto) {
  const pet = request.pets[0];
  if (!pet) return '';
  return [pet.name, petDetails(request)].filter(Boolean).join(', ');
}

function requestDates(request: TransportRequestDto) {
  return [dateLabel(request.pickup_not_before), dateLabel(request.delivery_not_after)].filter(Boolean).join(' – ');
}

function requestUrgency(value: string) {
  if (value === 'urgent') return 'Срочная';
  if (value === 'normal') return 'Обычная';
  return value;
}

function requestFeeding(value: string) {
  if (value === 'owner_food') return 'Корм с собой';
  if (value === 'carrier_food') return 'Кормит перевозчик';
  return value;
}

function requestContact(name: string | null, phone: string | null) {
  const clean = (value: string | null) => value?.trim().replace(/^[·•⋅]+\s*|\s*[·•⋅]+$/g, '') || '';
  return [clean(name), clean(phone)].filter(Boolean).join(' ');
}

function CheckpointMedia({ files, title }: { files: FileAssetDto[]; title: string }) {
  const photos = files.filter((file) => file.media_kind !== 'video' && !file.content_type?.startsWith('video/'));
  const gallery = photos.map((file, index) => ({
    url: file.url,
    alt: file.original_filename || `${title}, фото ${index + 1}`,
  }));

  return files.map((file) => {
    if (file.media_kind === 'video' || file.content_type?.startsWith('video/')) {
      return <video controls key={file.id} preload="metadata" src={file.url} />;
    }

    const photoIndex = photos.findIndex((photo) => photo.id === file.id);
    const alt = file.original_filename || `${title}, фото ${photoIndex + 1}`;
    return (
      <button
        aria-label={`Открыть ${alt} на весь экран`}
        className="carrier-active__photo-button"
        key={file.id}
        type="button"
        onClick={() => openPhotoGallery(gallery, photoIndex)}
      >
        <img src={file.url} alt={alt} />
      </button>
    );
  });
}

const CHECKPOINT_MAX_FILES = 6;
const CHECKPOINT_MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const CHECKPOINT_MAX_VIDEO_BYTES = 200 * 1024 * 1024;
const CHECKPOINT_ACCEPT = 'image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm';

type CheckpointFileKind = 'image' | 'video';

interface CheckpointDraftFile {
  file: File;
  id: string;
  kind: CheckpointFileKind;
  previewUrl: string;
}

interface CheckpointDraft {
  comment: string;
  files: File[];
  occurredAt: string;
  title: string;
}

function localDateTimeValue() {
  const date = new Date();
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
}

function checkpointFileKind(file: File): CheckpointFileKind | null {
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (file.type.startsWith('image/') || ['jpg', 'jpeg', 'png', 'webp'].includes(extension ?? '')) return 'image';
  if (file.type.startsWith('video/') || ['mp4', 'mov', 'webm'].includes(extension ?? '')) return 'video';
  return null;
}

function CheckpointModal({
  onClose,
  onCreate,
}: {
  onClose: () => void;
  onCreate: (draft: CheckpointDraft, onProgress: (percent: number) => void) => Promise<void>;
}) {
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [occurredAt, setOccurredAt] = useState(localDateTimeValue);
  const [files, setFiles] = useState<CheckpointDraftFile[]>([]);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const filesRef = useRef<CheckpointDraftFile[]>([]);
  const onCloseRef = useRef(onClose);
  const submittingRef = useRef(false);

  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  useEffect(() => {
    onCloseRef.current = onClose;
    submittingRef.current = submitting;
  }, [onClose, submitting]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus({ preventScroll: true });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !submittingRef.current) onCloseRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
      filesRef.current.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    };
  }, []);

  function chooseFiles(event: ChangeEvent<HTMLInputElement>) {
    const selected = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (!selected.length) return;
    if (files.length + selected.length > CHECKPOINT_MAX_FILES) {
      setError(`Можно прикрепить не более ${CHECKPOINT_MAX_FILES} файлов.`);
      return;
    }

    const prepared: CheckpointDraftFile[] = [];
    for (const file of selected) {
      const kind = checkpointFileKind(file);
      if (!kind) {
        prepared.forEach((item) => URL.revokeObjectURL(item.previewUrl));
        setError(`Файл «${file.name}» имеет неподдерживаемый формат.`);
        return;
      }
      const maxBytes = kind === 'video' ? CHECKPOINT_MAX_VIDEO_BYTES : CHECKPOINT_MAX_IMAGE_BYTES;
      if (file.size > maxBytes) {
        prepared.forEach((item) => URL.revokeObjectURL(item.previewUrl));
        setError(kind === 'video' ? 'Видео должно быть не больше 200 МБ.' : 'Фотография должна быть не больше 10 МБ.');
        return;
      }
      prepared.push({
        file,
        id: `${file.name}:${file.size}:${file.lastModified}:${crypto.randomUUID()}`,
        kind,
        previewUrl: URL.createObjectURL(file),
      });
    }
    setError('');
    setFiles((current) => [...current, ...prepared]);
  }

  function removeFile(id: string) {
    setFiles((current) => {
      const removed = current.find((item) => item.id === id);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return current.filter((item) => item.id !== id);
    });
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!title.trim()) {
      setError('Укажите название контрольной точки.');
      return;
    }
    if (!files.length) {
      setError('Добавьте хотя бы одну фотографию или видео.');
      return;
    }
    if (!occurredAt) {
      setError('Укажите дату и время контрольной точки.');
      return;
    }

    setError('');
    setProgress(0);
    setSubmitting(true);
    try {
      await onCreate({
        comment: comment.trim(),
        files: files.map((item) => item.file),
        occurredAt: new Date(occurredAt).toISOString(),
        title: title.trim(),
      }, setProgress);
      onClose();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Не удалось создать контрольную точку. Попробуйте ещё раз.');
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    <div className="checkpoint-modal__backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !submitting) onClose(); }}>
      <section ref={dialogRef} aria-labelledby="checkpoint-modal-title" aria-modal="true" className="checkpoint-modal" role="dialog" tabIndex={-1}>
        <header className="checkpoint-modal__header">
          <div>
            <h2 id="checkpoint-modal-title">Добавить контрольную точку</h2>
            <p>Расскажите заказчику, как проходит поездка, и приложите фото или видео.</p>
          </div>
          <button aria-label="Закрыть" disabled={submitting} type="button" onClick={onClose}>
            <X aria-hidden="true" size={24} strokeWidth={1.5} />
          </button>
        </header>

        <form onSubmit={submit}>
          <label className="checkpoint-modal__field">
            <span>Название этапа</span>
            <input disabled={submitting} maxLength={255} placeholder="Например: остановка в Туле" value={title} onChange={(event) => setTitle(event.target.value)} />
          </label>
          <label className="checkpoint-modal__field">
            <span>Дата и время</span>
            <input disabled={submitting} max={localDateTimeValue()} type="datetime-local" value={occurredAt} onChange={(event) => setOccurredAt(event.target.value)} />
          </label>
          <label className="checkpoint-modal__field">
            <span>Комментарий</span>
            <textarea disabled={submitting} maxLength={1000} placeholder="Состояние питомца, кормление, прогулка и другие важные детали" value={comment} onChange={(event) => setComment(event.target.value)} />
          </label>

          <div className="checkpoint-modal__media-heading">
            <strong>Фото и видео</strong>
            <span>{files.length}/{CHECKPOINT_MAX_FILES}</span>
          </div>
          {files.length ? (
            <div className="checkpoint-modal__previews">
              {files.map((item) => (
                <article key={item.id}>
                  {item.kind === 'video'
                    ? <video controls preload="metadata" src={item.previewUrl} />
                    : <img src={item.previewUrl} alt={item.file.name} />}
                  <button aria-label={`Удалить ${item.file.name}`} disabled={submitting} type="button" onClick={() => removeFile(item.id)}>
                    <Trash2 aria-hidden="true" size={18} />
                  </button>
                  <span title={item.file.name}>{item.file.name}</span>
                </article>
              ))}
            </div>
          ) : null}
          {files.length < CHECKPOINT_MAX_FILES ? (
            <button className="checkpoint-modal__add-media" disabled={submitting} type="button" onClick={() => fileInput.current?.click()}>
              <Camera aria-hidden="true" size={20} strokeWidth={1.5} />
              <Video aria-hidden="true" size={22} strokeWidth={1.5} />
              <span>Добавить фото или видео</span>
            </button>
          ) : null}
          <input ref={fileInput} accept={CHECKPOINT_ACCEPT} hidden multiple type="file" onChange={chooseFiles} />
          <p className="checkpoint-modal__hint">JPG, PNG, WEBP до 10 МБ; MP4, MOV, WEBM до 200 МБ. Не более 6 файлов.</p>
          {error ? <p className="checkpoint-modal__error" role="alert">{error}</p> : null}
          {submitting ? (
            <div className="checkpoint-modal__progress" aria-label={`Загрузка ${progress}%`}>
              <span style={{ width: `${progress}%` }} />
              <small>Загрузка: {progress}%</small>
            </div>
          ) : null}
          <footer className="checkpoint-modal__actions">
            <button className="carrier-outline-button" disabled={submitting} type="button" onClick={onClose}>Отмена</button>
            <button className="carrier-green-button" disabled={submitting} type="submit">Создать точку</button>
          </footer>
        </form>
      </section>
    </div>,
    document.body,
  );
}

function ActiveTransportView() {
  const { language } = useLanguage();
  const requestedId = hashNumber('id');
  const [allActive, setAllActive] = useState<TransportDto[]>([]);
  const [transport, setTransport] = useState<TransportDto | null>(null);
  const [request, setRequest] = useState<TransportRequestDto | null>(null);
  const [profile, setProfile] = useState<CarrierProfileDto | null>(null);
  const [hasReview, setHasReview] = useState(false);
  const [chatId, setChatId] = useState<number | null>(null);
  const [checkpointModalOpen, setCheckpointModalOpen] = useState(false);
  const loadVersion = useRef(0);
  const [state, setState] = useState<'loading' | 'ready' | 'submitting' | 'error'>('loading');

  async function load() {
    const version = ++loadVersion.current;
    const [transports, nextProfile, chats, reviews] = await Promise.all([
      carrierCabinetApi.transports(),
      carrierCabinetApi.profile(),
      carrierCabinetApi.chats(),
      carrierCabinetApi.reviews(),
    ]);
    const active = transports.filter(isActiveTransport);

    const requestedTransport = requestedId ? transports.find((item) => item.id === requestedId) : null;
    const nextTransport = requestedId
      ? (requestedTransport && (isActiveTransport(requestedTransport) || isCompletedTransport(requestedTransport)) ? requestedTransport : null)
      : active[0] ?? null;
    const nextRequest = nextTransport ? await carrierCabinetApi.request(nextTransport.request_id) : null;
    if (version !== loadVersion.current) return;
    setAllActive(active);
    setTransport(nextTransport);
    setRequest(nextRequest);
    setProfile(nextProfile);
    setHasReview(reviews.some(item => item.transport === nextTransport?.id && item.author_role === 'carrier'));
    setChatId(chats.find((item) => item.request === nextTransport?.request_id)?.id ?? null);
    setState('ready');
  }

  useEffect(() => {
    let active = true;
    setState('loading');
    load().catch(() => { if (active) setState('error'); });
    return () => { active = false; loadVersion.current += 1; };
  }, [requestedId]);

  async function changeTransportStatus() {
    if (!transport || !hasAcceptedOffer(transport) || state === 'submitting') return;
    setState('submitting');
    try {
      if (transport.status === 'planned') await carrierCabinetApi.startTransport(transport.id);
      else if (transport.status === 'in_progress') await carrierCabinetApi.deliverTransport(transport.id);
      await load();
    } catch {
      setState('error');
    }
  }

  async function addCheckpoint(draft: CheckpointDraft, onProgress: (percent: number) => void) {
    if (!transport) return;
    setState('submitting');
    const uploadedIds: number[] = [];
    let checkpointCreated = false;
    try {
      for (const [index, file] of draft.files.entries()) {
        const asset = await carrierCabinetApi.uploadCheckpointMedia(file, (fileProgress) => {
          onProgress(Math.round(((index + fileProgress / 100) / draft.files.length) * 100));
        });
        uploadedIds.push(asset.id);
      }
      await carrierCabinetApi.createCheckpoint(transport.id, {
        title: draft.title,
        comment: draft.comment,
        occurred_at: draft.occurredAt,
        media: uploadedIds,
      });
      checkpointCreated = true;
      await load();
    } catch (error) {
      if (!checkpointCreated) {
        await Promise.allSettled(uploadedIds.map((id) => carrierCabinetApi.deleteMedia(id)));
        setState('ready');
      } else {
        setState('error');
      }
      throw error;
    }
  }

  async function confirmPayment() {
    if (!transport || !hasAcceptedOffer(transport) || transport.payment_status !== 'customer_marked_paid' || state === 'submitting') return;
    setState('submitting');
    try {
      await carrierCabinetApi.confirmPayment(transport.deal);
      await load();
    } catch {
      setState('error');
    }
  }

  if (!transport || !request || !profile || state === 'loading' || (requestedId && transport.id !== requestedId)) {
    return (
      <div className="carrier-active" data-state={state}>
        <PageHeading
          className="carrier-active__top"
          figmaNode="2721:50532"
          title="Активная перевозка"
        />
        <CabinetLoadState state={state} />
        {state === 'ready' ? <>
          <CabinetEmptyState kind="transports" title={requestedId ? (language === 'en' ? 'Transport not found' : 'Перевозка не найдена') : undefined} />
          <button className="carrier-green-button" type="button" onClick={() => navigateCarrier('carrier-search')}>
            Найти заявку
          </button>
        </> : null}
      </div>
    );
  }

  const checkpoints = transport.checkpoints;
  const completed = isCompletedTransport(transport);
  return (
    <div className="carrier-active" data-state={state}>
      <PageHeading
        className="carrier-active__top"
        figmaNode="2721:50532"
        title={<><span>{completed ? 'Завершённая перевозка' : 'Активная перевозка'}</span><span className="carrier-active__status">{completed ? 'Завершено' : tripStatusLabel(transport)}</span></>}
        subtitle={
          <>
            <span>{transport.from_city} → {transport.to_city}</span><i />
            <span>#{transport.request_code}</span><i />
            <span>Заказчик <ProfileLink profileId={transport.customer_id} role="customer">{transport.customer_name}</ProfileLink></span>
          </>
        }
        action={
          <div className="carrier-active__top-actions">
            <button className="carrier-outline-button" disabled={!chatId} type="button" onClick={() => chatId && navigateCarrier('carrier-chat', { chat: chatId })}>
              Чат с заказчиком
            </button>
          </div>
        }
      />

      {allActive.length > 1 ? (
        <div className="carrier-active__tabs" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {allActive.map((item) => (
            <button
              key={item.id}
              className={`carrier-outline-button${item.id === transport.id ? ' is-active' : ''}`}
              style={{
                height: 36,
                padding: '0 14px',
                fontSize: 13,
                background: item.id === transport.id ? 'var(--lovo-green-light)' : 'transparent',
                borderColor: item.id === transport.id ? 'var(--lovo-green)' : 'var(--lovo-green-medium)',
              }}
              type="button"
              onClick={() => navigateCarrier('carrier-active-transport', { id: item.id })}
            >
              {item.from_city} → {item.to_city} (#{item.request_code})
            </button>
          ))}
        </div>
      ) : null}

      {!completed ? <><div className="carrier-active__alert" data-figma-node="2721:50556">
        Добавляйте контрольные точки с фото на каждом этапе — это повышает рейтинг фото и доверие заказчиков
      </div>

      <section className="carrier-active__card carrier-active__delivery" data-figma-node="2721:50625">
        <h2>Передали питомца? Закрываем сделку</h2>
        <p>После доставки заказчик подтвердит получение, и оплата поступит на ваш баланс</p>
        <button className="carrier-green-button" disabled={!hasAcceptedOffer(transport) || state === 'submitting' || !['planned', 'in_progress'].includes(transport.status)} type="button" onClick={() => void changeTransportStatus()}>
          {transport.status === 'planned' ? 'Начать перевозку' : <><Check aria-hidden="true" size={16} /> Я доставил питомца</>}
        </button>
      </section></> : null}

      {completed && (transport.status === 'closed' || (transport.status === 'delivered' && transport.customer_received_at))
        ? <section className="carrier-active__card carrier-customer-review" data-i18n-skip>
          <h2>{language === 'en' ? 'Review the customer' : 'Отзыв о заказчике'}</h2>
          <p>{hasReview ? (language === 'en' ? 'Your review has already been submitted.' : 'Ваш отзыв уже отправлен.') : (language === 'en' ? 'You can now leave a review of the customer' : 'Теперь вы можете оставить отзыв о заказчике')}</p>
          <button className="carrier-green-button" type="button" disabled={hasReview || state !== 'ready'} onClick={() => navigateCarrier('carrier-review', { transport: transport.id })}>
            {hasReview ? (language === 'en' ? 'Review submitted' : 'Отзыв оставлен') : (language === 'en' ? 'Leave a review' : 'Оставить отзыв')}
          </button>
        </section> : null}

      <section className="carrier-active__timeline" data-figma-node="2721:50557">
        <header>
          <strong>Контрольные точки перевозки <span><i /> {transport.checkpoints.length}</span></strong>
          <span>Рейтинг фото: {profile.photo_rating_percent}%</span>
        </header>
        <div className="carrier-active__connector" />
        <div className="carrier-active__points">
          {checkpoints.map((checkpoint, index) => (
            <article className={index === checkpoints.length - 1 ? 'is-current' : ''} key={checkpoint.id}>
              <span className="carrier-active__point" />
              <div>
                <time dateTime={checkpoint.occurred_at ?? checkpoint.created_at}>{dateTimeLabel(checkpoint.occurred_at ?? checkpoint.created_at)}</time>
                <h3>{checkpoint.title}</h3>
                <p>{checkpoint.comment}</p>
                <div className="carrier-active__photos">
                  <CheckpointMedia files={checkpoint.media_files ?? []} title={checkpoint.title} />
                </div>
              </div>
            </article>
          ))}
        </div>
        {!completed ? <button
          className="carrier-active__upload"
          disabled={!['in_progress', 'delivered'].includes(transport.status) || state === 'submitting'}
          title={transport.status === 'planned'
            ? 'Сначала начните перевозку'
            : transport.status === 'closed'
              ? 'Закрытая перевозка недоступна для изменений'
              : undefined}
          type="button"
          onClick={() => setCheckpointModalOpen(true)}
        >
          <Camera aria-hidden="true" size={16} strokeWidth={1.5} />
          <Video aria-hidden="true" size={20} strokeWidth={1.5} />
          <span>Добавить контрольную точку с фото или видео</span>
        </button> : null}
      </section>

      {checkpointModalOpen ? (
        <CheckpointModal onClose={() => setCheckpointModalOpen(false)} onCreate={addCheckpoint} />
      ) : null}

      <section className="carrier-active__card carrier-active__details" data-figma-node="2721:50629">
        <h2>Детали заявки</h2>
        <div className="carrier-active__kv">
          <ActiveTransportDetail label="Откуда" value={request.from_city} />
          <ActiveTransportDetail label="Куда" value={request.to_city} />
          <ActiveTransportDetail label="Питомец" value={petFullDetails(request)} />
          <ActiveTransportDetail label="Сроки" value={requestDates(request)} />
          <ActiveTransportDetail label="Срочность" value={requestUrgency(request.urgency)} />
          <ActiveTransportDetail label="Бюджет" value={budgetLabel(request) || 'Ожидает цену от перевозчика'} />
          <ActiveTransportDetail label="Переноска" value={carrierBoxLabel(request.carrier_box)} />
          <ActiveTransportDetail label="Документы" value={documentsStatusLabel(request.documents_status)} />
          <ActiveTransportDetail label="Кормление" value={requestFeeding(request.feeding_notes)} />
          <ActiveTransportDetail label="Контакт при заборе" value={requestContact(request.pickup_contact_name, request.pickup_contact_phone)} />
          <ActiveTransportDetail label="Контакт при доставке" value={requestContact(request.delivery_contact_name, request.delivery_contact_phone)} />
          <ActiveTransportDetail label="Ориентир при заборе" value={request.pickup_contact_notes} />
          <ActiveTransportDetail label="Ориентир при доставке" value={request.delivery_contact_notes} />
          <ActiveTransportDetail label="Особенности" value={request.pet_notes} />
        </div>
      </section>

      <section
        className={`carrier-active__card carrier-active__finance${transport.payment_status === 'customer_marked_paid' ? ' carrier-active__finance--confirmation' : ''}`}
        data-figma-node="2721:50644"
      >
        <h2>Финансы по перевозке</h2>
        <div className="carrier-active__breakdown">
          <div><span>К получению</span><strong>{money(transport.total_price_minor, transport.currency)}</strong></div>
          <div><span>Комиссия LOVO</span><strong>{money(transport.platform_fee_minor, transport.currency)}</strong></div>
          <div><span>Чистая прибыль</span><strong>{money(transport.net_price_minor, transport.currency)}</strong></div>
        </div>
        <div className="carrier-active__payment-status">
          <span>Статус оплаты</span><strong>{paymentStatusLabel(transport.payment_status)}</strong>
        </div>
        {transport.payment_status === 'customer_marked_paid' ? (
          <button className="carrier-green-button carrier-active__confirm-payment" disabled={state === 'submitting'} type="button" onClick={() => void confirmPayment()}>
            <Check aria-hidden="true" size={16} /> Я получил оплату{transport.customer_received_at ? ' — закрыть сделку' : ''}
          </button>
        ) : null}
      </section>
    </div>
  );
}

type HistoryTab = 'all' | 'year' | 'reviewed' | 'disputed';

function HistoryView() {
  const { language } = useLanguage();
  const [profile, setProfile] = useState<CarrierProfileDto | null>(null);
  const [transports, setTransports] = useState<TransportDto[]>([]);
  const [requests, setRequests] = useState(new Map<number, TransportRequestDto>());
  const [reviews, setReviews] = useState<ReviewDto[]>([]);
  const [disputes, setDisputes] = useState<DisputeDto[]>([]);
  const [tab, setTab] = useState<HistoryTab>('all');
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let active = true;
    const load = async () => {
      const [nextProfile, allTransports, nextReviews, nextDisputes] = await Promise.all([
        carrierCabinetApi.profile(),
        carrierCabinetApi.transports(),
        carrierCabinetApi.reviews(),
        carrierCabinetApi.disputes(),
      ]);
      const completed = allTransports.filter(isCompletedTransport);
      const entries = await Promise.all(completed.map(async (item) => [item.request_id, await carrierCabinetApi.request(item.request_id)] as const));
      if (!active) return;
      setProfile(nextProfile);
      setTransports(completed);
      setRequests(new Map(entries));
      setReviews(nextReviews);
      setDisputes(nextDisputes);
      setState('ready');
    };
    load().catch(() => { if (active) setState('error'); });
    return () => { active = false; };
  }, []);

  const customerReviews = useMemo(() => reviews.filter((item) => item.author_role === 'customer'), [reviews]);
  const dealDisputes = useMemo(() => new Set(disputes.map((item) => item.deal)), [disputes]);
  const year = new Date().getFullYear();
  const completedCount = Math.max(profile?.completed_transports_count ?? 0, transports.length);
  const tripWord = completedCount % 10 === 1 && completedCount % 100 !== 11 ? 'поездка'
    : completedCount % 10 >= 2 && completedCount % 10 <= 4 && (completedCount % 100 < 12 || completedCount % 100 > 14) ? 'поездки' : 'поездок';
  const counts = {
    all: transports.length,
    year: transports.filter((item) => new Date(item.delivered_at ?? item.created_at).getFullYear() === year).length,
    reviewed: transports.filter((item) => customerReviews.some((review) => review.transport === item.id)).length,
    disputed: transports.filter((item) => dealDisputes.has(item.deal)).length,
  };
  const visible = transports
    .filter((item) => {
      if (tab === 'year') return new Date(item.delivered_at ?? item.created_at).getFullYear() === year;
      if (tab === 'reviewed') return customerReviews.some((review) => review.transport === item.id);
      if (tab === 'disputed') return dealDisputes.has(item.deal);
      return true;
    })
    .sort((left, right) => new Date(right.delivered_at ?? right.created_at).getTime() - new Date(left.delivered_at ?? left.created_at).getTime());

  return (
    <div className="carrier-history" data-state={state}>
      <PageHeading
        className="carrier-history__heading"
        figmaNode="2691:64761"
        title="Все ваши перевозки"
        subtitle={
          <>
            <span data-i18n-skip>{language === 'en' ? `${completedCount} ${completedCount === 1 ? 'trip' : 'trips'} completed` : `Завершено ${completedCount} ${tripWord}`}</span>
            <span data-i18n-skip>{language === 'en' ? 'Average rating' : 'Средняя оценка'} ★ {Number(profile?.review_rating ?? 0).toFixed(1)}</span>
          </>
        }
      />
      <div className="carrier-history__tabs" data-figma-node="2699:66388" role="tablist" aria-label="Фильтр истории">
        {([
          ['all', 'Все'],
          ['year', 'Этот год'],
          ['reviewed', 'С отзывом'],
          ['disputed', 'Спорные'],
        ] as Array<[HistoryTab, string]>).map(([value, label]) => (
          <button className={tab === value ? 'is-active' : ''} key={value} role="tab" aria-selected={tab === value} type="button" onClick={() => setTab(value)}>
            {label} <i /> {counts[value]}
          </button>
        ))}
      </div>
      <div className="carrier-history__list" data-figma-node="2691:64796">
        {visible.map((transport) => {
          const request = requests.get(transport.request_id);
          if (!request) return null;
          const review = customerReviews.find((item) => item.transport === transport.id);
          const departure = dateLabel(transport.departure_date);
          const delivery = dateLabel(transport.delivery_date);
          return (
            <article className="carrier-history__row" key={transport.id}>
              <button type="button" aria-label={`Открыть перевозку ${transport.request_code}`} onClick={() => navigateCarrier('carrier-active-transport', { id: transport.id })} />
              <div className="carrier-history__body">
                <div className="carrier-history__top-row">
                  <div>
                    <strong>{transport.from_city} → {transport.to_city}</strong><i />
                    <span>#{transport.request_code}</span>
                    {review ? <em>★ {review.rating_overall.toFixed(1)}</em> : null}
                  </div>
                  <span>Завершено</span>
                </div>
                <div className="carrier-history__bottom-row">
                  <div><span>{petDetails(request)}</span><i /><span><ProfileLink profileId={transport.customer_id} role="customer">{transport.customer_name}</ProfileLink></span><i /><span>{departure && delivery ? `${departure} – ${delivery}` : departure || delivery}</span></div>
                  <strong>{money(transport.net_price_minor, transport.currency)}</strong>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

type InvoiceTab = 'all' | 'waiting' | 'paid' | 'refunded' | 'overdue';

function transactionLabel(transaction: BillingTransactionDto, invoice?: InvoiceDto) {
  const code = invoice ? ` #${invoice.public_code}` : '';
  if (transaction.transaction_type === 'invoice_issued') return `Выставлен счёт комиссии${code}`;
  if (transaction.transaction_type === 'invoice_paid') return `Оплачен счёт комиссии${code}`;
  if (transaction.transaction_type === 'balance_charge') return `Списание счёта комиссии${code}`;
  if (transaction.transaction_type === 'refund_approved') return `Возврат по счёту${code}`;
  if (transaction.transaction_type === 'balance_withdrawal') return 'Вывод с внутреннего баланса';
  if (transaction.transaction_type === 'test_top_up') return 'Тестовое пополнение баланса';
  return transaction.transaction_type;
}

function invoiceStatusLabel(status: string) {
  if (status === 'paid') return 'Оплачен';
  if (status === 'overdue') return 'Просрочен';
  if (status === 'refunded') return 'Возвращён';
  if (status === 'cancelled') return 'Отменён';
  return 'Ожидает';
}

function EarningsView() {
  const { language } = useLanguage();
  const [profile, setProfile] = useState<CarrierProfileDto | null>(null);
  const [summary, setSummary] = useState<BillingSummaryDto | null>(null);
  const [invoices, setInvoices] = useState<InvoiceDto[]>([]);
  const [transactions, setTransactions] = useState<BillingTransactionDto[]>([]);
  const [completedCount, setCompletedCount] = useState(0);
  const [tab, setTab] = useState<InvoiceTab>('all');
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('1000');
  const [topUpError, setTopUpError] = useState('');
  const [state, setState] = useState<'loading' | 'ready' | 'submitting' | 'error'>('loading');

  async function load() {
    const [nextSummary, nextInvoices, nextTransactions, transports, nextProfile] = await Promise.all([
      carrierCabinetApi.billing(),
      carrierCabinetApi.invoices(),
      carrierCabinetApi.transactions(),
      carrierCabinetApi.transports(),
      carrierCabinetApi.profile(),
    ]);
    setProfile(nextProfile);
    setSummary(nextSummary);
    setInvoices(nextInvoices);
    setTransactions(nextTransactions);
    setCompletedCount(transports.filter((item) => ['delivered', 'closed'].includes(item.status)).length);
    setState('ready');
  }

  useEffect(() => {
    load().catch(() => setState('error'));
  }, []);

  useEffect(() => {
    if (!topUpOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && state !== 'submitting') setTopUpOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [state, topUpOpen]);

  const openInvoice = summary?.open_invoices[0] ?? null;
  const topUpCurrency = profile?.display_currency ?? summary?.balance.currency ?? 'RUB';
  const topUpCurrencySymbol = topUpCurrency === 'EUR' ? '€' : '₽';
  const openInvoiceAmount = openInvoice ? money(openInvoice.amount_minor, openInvoice.currency) : '';
  const invoiceDownloadLabel = language === 'en' ? 'Download invoice PDF' : 'Скачать PDF-счёт';
  const invoiceMap = useMemo(() => new Map(invoices.map((item) => [item.id, item])), [invoices]);
  const paidCommission = invoices.filter((item) => item.status === 'paid').reduce((sum, item) => sum + item.amount_minor, 0);
  const counts = {
    all: invoices.length,
    waiting: invoices.filter((item) => item.status === 'issued').length,
    paid: invoices.filter((item) => item.status === 'paid').length,
    refunded: invoices.filter((item) => item.status === 'refunded').length,
    overdue: invoices.filter((item) => item.status === 'overdue').length,
  };
  const visibleInvoices = invoices.filter((item) => {
    if (tab === 'waiting') return item.status === 'issued';
    if (tab === 'paid') return item.status === 'paid';
    if (tab === 'refunded') return item.status === 'refunded';
    if (tab === 'overdue') return item.status === 'overdue';
    return true;
  });

  function contactSupport(subject: string) {
    if (summary?.support_email) {
      window.location.href = `mailto:${summary.support_email}?subject=${encodeURIComponent(subject)}`;
      return;
    }
    navigateCarrier('carrier-help');
  }

  function downloadRequisites() {
    const rows = [
      ['Получатель', requisites.recipient],
      ['ИНН', requisites.tax_id],
      ['КПП', requisites.kpp],
      ['Р/с', requisites.account],
      ['Банк', requisites.bank],
      ['Назначение платежа', requisites.payment_purpose],
    ].filter(([, value]) => value);
    if (!rows.length) return;
    const blob = new Blob([rows.map(([label, value]) => `${label}: ${value}`).join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'LOVO-requisites.txt';
    link.click();
    URL.revokeObjectURL(url);
  }

  async function payFromBalance() {
    if (!openInvoice) return;
    setState('submitting');
    try {
      await carrierCabinetApi.payInvoiceFromBalance(openInvoice.id);
      await load();
    } catch {
      setState('error');
    }
  }

  async function submitTopUp(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const amount = Number(topUpAmount.replace(',', '.'));
    if (!Number.isInteger(amount) || amount < 1 || amount > 1_000_000) {
      setTopUpError(language === 'en'
        ? `Enter a whole amount from 1 to 1,000,000 ${topUpCurrencySymbol}.`
        : `Введите целую сумму от 1 до 1 000 000 ${topUpCurrencySymbol}.`);
      return;
    }
    setTopUpError('');
    setState('submitting');
    try {
      await carrierCabinetApi.testTopUpBalance(amount * 100);
      await load();
      setTopUpOpen(false);
      setTopUpAmount('1000');
    } catch {
      setTopUpError('Не удалось пополнить баланс. Попробуйте ещё раз.');
      setState('ready');
    }
  }

  const DEFAULT_REQUISITES = {
    recipient: 'ООО «ЛОВО СЕРВИС»',
    tax_id: '7712345678',
    kpp: '771201001',
    account: '40702810900000012345',
    bank: 'АО «Тинькофф Банк»',
    payment_purpose: 'Оплата счёта №LOVO-XXXX-COM',
  };

  if (!summary) return <div className="carrier-earnings" data-state={state} />;
  const requisites = {
    recipient: summary.payment_requisites?.recipient || DEFAULT_REQUISITES.recipient,
    tax_id: summary.payment_requisites?.tax_id || DEFAULT_REQUISITES.tax_id,
    kpp: summary.payment_requisites?.kpp || DEFAULT_REQUISITES.kpp,
    account: summary.payment_requisites?.account || DEFAULT_REQUISITES.account,
    bank: summary.payment_requisites?.bank || DEFAULT_REQUISITES.bank,
    payment_purpose: summary.payment_requisites?.payment_purpose || DEFAULT_REQUISITES.payment_purpose,
  };

  return (
    <div className="carrier-earnings" data-state={state}>
      <PageHeading
        className="carrier-earnings__top"
        figmaNode="2699:66775"
        title="Деньги и счета"
        subtitle="С каждой перевозки удерживается 5% — счёт формируется автоматически и должен быть оплачен для новых откликов"
      />

      <section className="carrier-earnings__card carrier-earnings__balance" data-figma-node="92:49227">
        <h2>Внутренний баланс LOVO</h2>
        <strong>{money(summary.balance.available_minor, summary.balance.currency)}</strong>
        <div>
          <button className="carrier-green-button" type="button" onClick={() => { setTopUpError(''); setTopUpOpen(true); }}>Пополнить баланс</button>
          <button className="carrier-outline-button" type="button" onClick={() => contactSupport('Вывод средств с внутреннего баланса LOVO')}>Вывести на карту через поддержку</button>
        </div>
      </section>

      {topUpOpen ? createPortal(
        <div className="balance-top-up__backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && state !== 'submitting') setTopUpOpen(false); }}>
          <section aria-labelledby="balance-top-up-title" aria-modal="true" className="balance-top-up" role="dialog">
            <header>
              <div>
                <h2 id="balance-top-up-title">Пополнить баланс</h2>
                <p>Тестовое пополнение без списания денежных средств</p>
              </div>
              <button aria-label="Закрыть" disabled={state === 'submitting'} type="button" onClick={() => setTopUpOpen(false)}>
                <X aria-hidden="true" size={24} strokeWidth={1.5} />
              </button>
            </header>
            <form onSubmit={(event) => void submitTopUp(event)}>
              <label>
                <span>Сумма пополнения</span>
                <div><input autoFocus disabled={state === 'submitting'} inputMode="numeric" max="1000000" min="1" step="1" type="number" value={topUpAmount} onChange={(event) => setTopUpAmount(event.target.value)} /><strong>{topUpCurrencySymbol}</strong></div>
              </label>
              <small>После подтверждения сумма будет сразу записана во внутренний баланс и историю операций.</small>
              {topUpError ? <p className="balance-top-up__error" data-i18n-skip role="alert">{topUpError}</p> : null}
              <div className="balance-top-up__actions">
                <button className="carrier-outline-button" disabled={state === 'submitting'} type="button" onClick={() => setTopUpOpen(false)}>Отмена</button>
                <button className="carrier-green-button" disabled={state === 'submitting'} type="submit">{state === 'submitting' ? 'Пополняем…' : 'Пополнить баланс'}</button>
              </div>
            </form>
          </section>
        </div>,
        document.body,
      ) : null}

      {openInvoice ? (
        <section className="carrier-earnings__card carrier-earnings__open" data-figma-node="92:49233">
          <h2>
            <span>{unpaidInvoiceTitle(openInvoice.public_code, language)}</span>
            <span className="carrier-earnings__dot">·</span>
            <span>{openInvoiceAmount}</span>
          </h2>
          <p>Пока счёт не оплачен, вы не можете отправлять новые отклики на заявки</p>
          <div>
            <button className="carrier-red-button" type="button" onClick={() => contactSupport(`Оплата счёта ${openInvoice.public_code}`)}>{payInvoiceByCardLabel(openInvoiceAmount, language)}</button>
            <button className="carrier-outline-button" type="button" onClick={() => void payFromBalance()}>Списать с внутреннего баланса</button>
            <a className="carrier-outline-button carrier-earnings__invoice-download" href={carrierCabinetApi.invoicePdfUrl(openInvoice.id)}>
              <Download aria-hidden="true" size={16} /> {invoiceDownloadLabel}
            </a>
          </div>
        </section>
      ) : null}

      <section className="carrier-earnings__info" data-figma-node="92:49242">
        <h2>Как это работает</h2>
        <ul>
          <li>С каждой завершённой перевозки удерживается комиссия LOVO 5%</li>
          <li>На сумму комиссии автоматически формируется счёт</li>
          <li>Пока счёт не оплачен — новые отклики отправлять нельзя</li>
          <li>Оплатить можно картой или списанием с внутреннего баланса</li>
        </ul>
      </section>

      <section className="carrier-earnings__stats" data-figma-node="92:49249">
        <div><strong>{money(paidCommission, summary.balance.currency)}</strong><span>Комиссия уплачена всего</span></div>
        <div><strong>{completedCount}</strong><span>Перевозок завершено</span></div>
      </section>

      <section className="carrier-earnings__card carrier-earnings__transactions" data-figma-node="92:49256">
        <h2>Движение внутреннего баланса</h2>
        <div className="carrier-earnings__transaction-table">
          <div className="is-header"><span>Дата</span><span>Операция</span><span>Сумма</span></div>
          {transactions.slice(0, 3).map((transaction) => (
            <div key={transaction.id}>
              <span>{shortDate(transaction.created_at)}</span>
              <span>{transactionLabel(transaction, transaction.invoice ? invoiceMap.get(transaction.invoice) : undefined)}</span>
              <strong className={transaction.amount_minor >= 0 ? 'is-positive' : ''}>{transaction.amount_minor > 0 ? '+' : ''}{money(transaction.amount_minor, transaction.currency)}</strong>
            </div>
          ))}
        </div>
      </section>

      <section className="carrier-earnings__card carrier-earnings__requisites" data-figma-node="92:49287">
        <h2>Реквизиты LOVO</h2>
        <div className="carrier-earnings__requisite-grid">
          <div><span>Получатель</span><strong>{requisites.recipient}</strong></div>
          <div><span>ИНН</span><strong>{requisites.tax_id}</strong></div>
          <div><span>КПП</span><strong>{requisites.kpp}</strong></div>
          <div><span>Р/с</span><strong>{requisites.account}</strong></div>
          <div><span>Банк</span><strong>{requisites.bank}</strong></div>
          <div><span>Назначение платежа</span><strong>{requisites.payment_purpose}</strong></div>
        </div>
        <button className="carrier-outline-button carrier-earnings__download-btn" data-figma-node="92:49226" type="button" onClick={downloadRequisites}><Download aria-hidden="true" size={16} /> Скачать реквизиты LOVO</button>
      </section>

      <section className="carrier-earnings__card carrier-earnings__history" data-figma-node="92:49309">
        <h2>История счетов · {invoices.length}</h2>
        <div className="carrier-earnings__tabs" role="tablist" aria-label="Фильтр счетов">
          {([
            ['all', 'Все'], ['waiting', 'Ожидают'], ['paid', 'Оплачены'], ['refunded', 'Возвращены'], ['overdue', 'Просрочены'],
          ] as Array<[InvoiceTab, string]>).map(([value, label]) => (
            <button className={tab === value ? 'is-active' : ''} key={value} role="tab" aria-selected={tab === value} type="button" onClick={() => setTab(value)}>{label} <i /> {counts[value]}</button>
          ))}
        </div>
        <div className="carrier-earnings__invoice-table">
          <div className="is-header"><span>Выставлен</span><span>№ счёта</span><span>Заявка</span><span>Сумма счёта</span><span>Срок</span><span>Статус</span></div>
          {visibleInvoices.slice(0, 4).map((invoice) => (
            <div key={invoice.id}>
              <span>{shortDate(invoice.created_at)}</span>
              <div className="carrier-earnings__invoice-code">
                <span>{invoice.public_code}</span>
                <a
                  aria-label={`${invoiceDownloadLabel} ${invoice.public_code}`}
                  className="carrier-earnings__invoice-download-icon"
                  href={carrierCabinetApi.invoicePdfUrl(invoice.id)}
                  title={invoiceDownloadLabel}
                >
                  <Download aria-hidden="true" size={16} />
                </a>
              </div>
              <span>#{invoice.request_code}</span>
              <span>{money(invoice.amount_minor, invoice.currency)}</span><span>{shortDate(invoice.due_date)}</span><strong className={`is-${invoice.status}`}>{invoiceStatusLabel(invoice.status)}</strong>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function CarrierWorkScreen({ screen }: { screen: CarrierWorkScreenId }) {
  return (
    <AuthGuard allow={['carrier']}>
      <CarrierCabinetLayout figmaNode={FIGMA_NODES[screen]} screen={screen}>
        {screen === 'carrier-active-transport' ? <ActiveTransportView /> : null}
        {screen === 'carrier-completed' ? <HistoryView /> : null}
        {screen === 'carrier-earnings' ? <EarningsView /> : null}
      </CarrierCabinetLayout>
    </AuthGuard>
  );
}
